/**
 * Timeline Audio Clip Component (canvas-v2)
 *
 * Audio clip block with purple styling.
 * Supports click to select, drag edges to trim, free-position drag (startOffset),
 * cross-track drag (trackIndex), and speed badge.
 */

import { useCallback, useState, useRef, memo, useSyncExternalStore } from 'react'
import { useTranslation } from 'react-i18next'
import type { TimelineAudioClip as TimelineAudioClipData, TimelineClipOperationState } from './types'
import { AlertTriangle, Loader2, VolumeX } from 'lucide-react'
import { pixelsToSeconds, secondsToPixels } from './timelineUtils'
import { StaticWaveform } from './StaticWaveform'
import { TimelineVolumeHandle } from './TimelineVolumeHandle'
import {
  editClipToTimelineAudio,
  timelineAudioToEditClip,
  trimAudioEditLeftByTimelineDelta,
  trimAudioEditRightByTimelineDelta,
} from '../../audio-edit/audioEditMath'
import {
  getMultiDragPreview,
  setMultiDragPreview,
  subscribeMultiDragPreview,
} from './multiDragPreviewStore'
import { createNadDebug } from '../../../../utils/debugLog'
import { emitTimelineNadEvent } from '../../audio-edit/nadTimelineLog'

const audioTrimLog = createNadDebug('dw:audio-editor:trim-math')
const interactionLog = createNadDebug('dw:timeline-playback:render-contract')
const selectionLog = createNadDebug('dw:timeline-playback:render-contract')

interface TimelineAudioClipProps {
  /** Audio clip data */
  clip: TimelineAudioClipData

  /** Zoom level (pixels per second) */
  zoomLevel: number

  /** Whether this clip is selected */
  isSelected: boolean

  /** Whether this clip is part of an active multi-selection (>1 selected) */
  isMultiSelectionMember?: boolean

  /** Callback when clip is clicked (replace selection) */
  onSelect: (clipId: string) => void

  /** Callback to place the playhead at the clicked point before Split */
  onSetPlayheadFromClick?: (clip: TimelineAudioClipData, requestedTime: number) => void

  /** Callback when clip is shift-clicked (toggle membership in multi-selection) */
  onToggleSelect?: (clipId: string) => void

  /** Multi-drag commit: applies a uniform startOffset delta + optional track delta to ALL selected clips */
  onMultiDragOffset?: (deltaSec: number, trackDelta?: number) => void

  /** Callback when clip is trimmed */
  onTrim: (clipId: string, trimStart: number, trimEnd: number, startOffset?: number) => void

  /** Callback for live trim preview (no history push) */
  onTrimPreview?: (clipId: string, trimStart: number, trimEnd: number, startOffset?: number) => void

  /** Callback when startOffset changes (free-position drag) */
  onOffsetChange: (clipId: string, startOffset: number) => void

  /** Callback when track index changes (cross-track drag) */
  onTrackIndexChange?: (clipId: string, newTrackIndex: number) => void

  /** Total number of audio tracks (for cross-track bounds) */
  audioTrackCount?: number

  /** Track height in pixels (for cross-track detection) */
  trackHeight?: number

  /** Whether timeline is locked (no editing) */
  locked?: boolean

  /** Allow click selection even when the clip is locked against timeline edits. */
  selectableWhenLocked?: boolean

  /** Operation state shown as a small clip-local icon. */
  operationState?: TimelineClipOperationState

  /** Inline volume preview callback (no history push). */
  onVolumePreview?: (clipId: string, volume: number) => void

  /** Inline volume commit callback (one history entry). */
  onVolumeCommit?: (clipId: string, volume: number, baselineVolume: number) => void
}

export const TimelineAudioClipComponent = memo(function TimelineAudioClipComponent({
  clip,
  zoomLevel,
  isSelected,
  isMultiSelectionMember = false,
  onSelect,
  onSetPlayheadFromClick,
  onToggleSelect,
  onMultiDragOffset,
  onTrim,
  onTrimPreview,
  onOffsetChange,
  onTrackIndexChange,
  audioTrackCount = 1,
  trackHeight = 60,
  locked = false,
  selectableWhenLocked = false,
  operationState,
  onVolumePreview,
  onVolumeCommit,
}: TimelineAudioClipProps) {
  const { t } = useTranslation('canvas')
  const [isDragging, setIsDragging] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const [dragType, setDragType] = useState<'move' | 'trim-start' | 'trim-end' | null>(null)
  const clipRef = useRef<HTMLDivElement>(null)
  const dragStartRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 })
  const startOffsetAtDragStart = useRef(clip.startOffset)
  const trackIndexAtDragStart = useRef(clip.trackIndex)

  const effectiveDuration = clip.effectiveDuration
  const plannedWindowS =
    typeof clip.plannedEndOffset === 'number' && Number.isFinite(clip.plannedEndOffset)
      ? Math.max(0, clip.plannedEndOffset - clip.startOffset)
      : null
  const displayLabel = clip.label?.trim() || clip.sourcePath.split('/').pop() || 'Audio'
  const width = secondsToPixels(effectiveDuration, zoomLevel)
  const waveformSourceWidth = Math.max(width, secondsToPixels(clip.originalDuration / clip.speed, zoomLevel))
  const waveformOffset = secondsToPixels(clip.trimStart / clip.speed, zoomLevel)
  const EDGE_RESIZE_WIDTH = 8
  const isOperationRunning = operationState?.status === 'running'
  const isEditLocked = locked || isOperationRunning

  const handleMouseDown = useCallback(
    (e: React.MouseEvent, type: 'move' | 'trim-start' | 'trim-end') => {
      if (isEditLocked) return
      // Only respond to primary (left) mouse button. Right/middle must NOT drag.
      if (e.button !== 0) {
        interactionLog({
          event: 'clip-drag-rejected',
          clipId: clip.id,
          button: e.button,
          type,
        })
        return
      }

      e.preventDefault()
      e.stopPropagation()

      setIsDragging(true)
      setDragType(type)
      dragStartRef.current = { x: e.clientX, y: e.clientY }
      startOffsetAtDragStart.current = clip.startOffset
      trackIndexAtDragStart.current = clip.trackIndex
      let workingClip = clip
      let finalTrimmedClip: TimelineAudioClipData | null = null

      // Snapshot multi-selection membership at drag start. For multi-drag we
      // defer the commit to mouseUp so all selected clips animate together.
      const dragAsMultiSelection = isMultiSelectionMember
      let pendingMultiDeltaSec = 0
      let pendingMultiTrackDelta = 0

      if (type === 'move' && !isSelected && !e.shiftKey) {
        onSelect(clip.id)
      }

      if (type === 'move' && dragAsMultiSelection) {
        setMultiDragPreview({ activeClipId: clip.id, deltaPx: 0, deltaPxY: 0 })
        interactionLog({
          event: 'multi-drag-preview-begin',
          activeClipId: clip.id,
          clipType: 'audio',
        })
      }

      const handleMouseMove = (moveEvent: MouseEvent) => {
        const deltaX = moveEvent.clientX - dragStartRef.current.x
        const deltaY = moveEvent.clientY - dragStartRef.current.y

        if (type === 'move') {
          if (dragAsMultiSelection && onMultiDragOffset) {
            // Multi-drag: don't mutate per-clip state during move (snap-on
            // -mouseup acceptable per user decision). Just remember the delta
            // so we can apply it once on mouseUp, AND broadcast a live preview
            // so other selected clips render the same transform.
            pendingMultiDeltaSec = pixelsToSeconds(deltaX, zoomLevel)
            pendingMultiTrackDelta = audioTrackCount > 1
              ? Math.round(deltaY / trackHeight)
              : 0
            setMultiDragPreview({
              activeClipId: clip.id,
              deltaPx: deltaX,
              deltaPxY: deltaY,
            })
            return
          }
          const deltaSec = pixelsToSeconds(deltaX, zoomLevel)
          const newOffset = Math.max(0, startOffsetAtDragStart.current + deltaSec)

          if (onTrackIndexChange && audioTrackCount > 1) {
            const trackDelta = Math.round(deltaY / trackHeight)
            const newTrackIndex = Math.max(
              0,
              Math.min(audioTrackCount - 1, trackIndexAtDragStart.current + trackDelta)
            )
            if (newTrackIndex !== clip.trackIndex) {
              onTrackIndexChange(clip.id, newTrackIndex)
            }
          }

          // Apply offset after any track change so normalization uses the destination track.
          onOffsetChange(clip.id, newOffset)
        } else if (type === 'trim-start') {
          const deltaSec = pixelsToSeconds(deltaX, zoomLevel)
          const trimmed = editClipToTimelineAudio(
            trimAudioEditLeftByTimelineDelta(timelineAudioToEditClip(workingClip), deltaSec),
            workingClip,
          )
          const logPayload = {
            event: 'timeline-audio-trim-preview',
            clipId: clip.id,
            edge: 'start',
            deltaS: Number(deltaSec.toFixed(3)),
            trimStartS: Number(trimmed.trimStart.toFixed(3)),
            trimEndS: Number(trimmed.trimEnd.toFixed(3)),
            startOffsetS: Number(trimmed.startOffset.toFixed(3)),
            effectiveDurationS: Number(trimmed.effectiveDuration.toFixed(3)),
            speed: Number(trimmed.speed.toFixed(3)),
          }
          audioTrimLog(logPayload)
          emitTimelineNadEvent('dw:audio-editor:trim-math', logPayload)
          const trimPreview = onTrimPreview || onTrim
          trimPreview(clip.id, trimmed.trimStart, trimmed.trimEnd, trimmed.startOffset)
          finalTrimmedClip = trimmed
          workingClip = trimmed
          dragStartRef.current = { x: moveEvent.clientX, y: moveEvent.clientY }
        } else if (type === 'trim-end') {
          const deltaSec = pixelsToSeconds(deltaX, zoomLevel)
          const trimmed = editClipToTimelineAudio(
            trimAudioEditRightByTimelineDelta(timelineAudioToEditClip(workingClip), deltaSec),
            workingClip,
          )
          const logPayload = {
            event: 'timeline-audio-trim-preview',
            clipId: clip.id,
            edge: 'end',
            deltaS: Number(deltaSec.toFixed(3)),
            trimStartS: Number(trimmed.trimStart.toFixed(3)),
            trimEndS: Number(trimmed.trimEnd.toFixed(3)),
            startOffsetS: Number(trimmed.startOffset.toFixed(3)),
            effectiveDurationS: Number(trimmed.effectiveDuration.toFixed(3)),
            speed: Number(trimmed.speed.toFixed(3)),
          }
          audioTrimLog(logPayload)
          emitTimelineNadEvent('dw:audio-editor:trim-math', logPayload)
          const trimPreview = onTrimPreview || onTrim
          trimPreview(clip.id, trimmed.trimStart, trimmed.trimEnd, trimmed.startOffset)
          finalTrimmedClip = trimmed
          workingClip = trimmed
          dragStartRef.current = { x: moveEvent.clientX, y: moveEvent.clientY }
        }
      }

      const handleMouseUp = () => {
        // Always end the multi-drag preview before the commit.
        if (type === 'move' && dragAsMultiSelection) {
          interactionLog({
            event: 'multi-drag-preview-end',
            activeClipId: clip.id,
            clipType: 'audio',
            committed:
              Math.abs(pendingMultiDeltaSec) > 1e-3 || pendingMultiTrackDelta !== 0,
          })
          setMultiDragPreview(null)
        }

        if ((type === 'trim-start' || type === 'trim-end') && finalTrimmedClip) {
          const commitPayload = {
            event: 'timeline-audio-trim-commit',
            clipId: clip.id,
            edge: type === 'trim-start' ? 'start' : 'end',
            trimStartS: Number(finalTrimmedClip.trimStart.toFixed(3)),
            trimEndS: Number(finalTrimmedClip.trimEnd.toFixed(3)),
            startOffsetS: Number(finalTrimmedClip.startOffset.toFixed(3)),
            effectiveDurationS: Number(finalTrimmedClip.effectiveDuration.toFixed(3)),
            speed: Number(finalTrimmedClip.speed.toFixed(3)),
          }
          audioTrimLog(commitPayload)
          emitTimelineNadEvent('dw:audio-editor:trim-math', commitPayload)
          onTrim(clip.id, finalTrimmedClip.trimStart, finalTrimmedClip.trimEnd, finalTrimmedClip.startOffset)
        } else if (type === 'move' && dragAsMultiSelection && onMultiDragOffset) {
          // Commit deferred multi-drag now (snap-on-mouseup).
          if (Math.abs(pendingMultiDeltaSec) > 1e-3 || pendingMultiTrackDelta !== 0) {
            onMultiDragOffset(pendingMultiDeltaSec, pendingMultiTrackDelta)
          }
        }

        setIsDragging(false)
        setDragType(null)

        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
      }

      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    },
    [isEditLocked, clip, zoomLevel, isSelected, isMultiSelectionMember, onSelect, onMultiDragOffset, onTrim, onTrimPreview, onOffsetChange, onTrackIndexChange, audioTrackCount, trackHeight]
  )

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if ((locked && !selectableWhenLocked) || isDragging) return
      e.stopPropagation()
      if (e.shiftKey && onToggleSelect) {
        // SHORTCUT_ID: timeline.multiSelectClip (keyboardShortcuts.ts — Keyboard Shortcuts modal)
        onToggleSelect(clip.id)
        selectionLog({ event: 'shift-click-toggle', clipId: clip.id, clipType: 'audio' })
      } else {
        const rect = e.currentTarget.getBoundingClientRect()
        const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / Math.max(1, rect.width)))
        onSetPlayheadFromClick?.(clip, clip.startOffset + clip.effectiveDuration * ratio)
        if (!isSelected) {
          onSelect(clip.id)
        } else {
          // Plain click on an already-selected clip: preserve selection.
          selectionLog({
            event: 'selection-preserved-on-click',
            clipId: clip.id,
            clipType: 'audio',
          })
        }
      }
    },
    [locked, selectableWhenLocked, isDragging, clip, isSelected, onSelect, onSetPlayheadFromClick, onToggleSelect]
  )

  const handleEdgeMouseDown = useCallback(
    (e: React.MouseEvent, edge: 'start' | 'end') => {
      e.stopPropagation()
      handleMouseDown(e, edge === 'start' ? 'trim-start' : 'trim-end')
    },
    [handleMouseDown]
  )

  // Subscribe to multi-drag preview. Audio multi-drag defers the offset
  // commit to mouseup, so during drag we apply the live transform to BOTH
  // the grabbed clip and other selected members (the underlying
  // startOffset / left isn't moving yet — only this transform makes the
  // group follow the cursor).
  const multiDragPreview = useSyncExternalStore(
    subscribeMultiDragPreview,
    getMultiDragPreview,
    getMultiDragPreview
  )
  const isMultiDragActive =
    isMultiSelectionMember && multiDragPreview !== null
  const isPreviewGhost =
    isMultiDragActive && multiDragPreview!.activeClipId !== clip.id
  const previewTransform = isMultiDragActive
    ? `translate(${multiDragPreview!.deltaPx}px, ${multiDragPreview!.deltaPxY}px)`
    : 'none'

  return (
    <div
      ref={clipRef}
      className={`
        absolute h-full
        ${clip.muted ? 'bg-surface-2/70 border-border' : 'bg-purple-900/60 border-purple-500'}
        border
        rounded
        overflow-hidden
        ${isSelected ? 'ring-2 ring-primary/90 ring-offset-1 ring-offset-background' : ''}
        ${isMultiSelectionMember ? 'ring-blue-300 shadow-[0_0_12px_rgba(96,165,250,0.45)]' : ''}
        ${isOperationRunning ? 'cursor-wait' : isDragging && dragType === 'move' ? 'cursor-grabbing' : locked ? (selectableWhenLocked ? 'cursor-pointer' : 'cursor-not-allowed') : 'cursor-grab'}
        ${isPreviewGhost ? 'opacity-50' : ''}
      `}
      style={{
        width: `${width}px`,
        left: `${secondsToPixels(clip.startOffset, zoomLevel)}px`,
        transform: previewTransform,
      }}
      data-testid="timeline-audio-clip"
      data-clip-id={clip.id}
      data-track-index={clip.trackIndex}
      data-start-offset={clip.startOffset.toFixed(3)}
      data-start-sec={clip.startOffset.toFixed(3)}
      data-selected={isSelected ? 'true' : 'false'}
      data-multi-drag-preview={isPreviewGhost ? 'true' : 'false'}
      data-trim-start={clip.trimStart.toFixed(3)}
      data-trim-end={clip.trimEnd.toFixed(3)}
      data-effective-duration={clip.effectiveDuration.toFixed(3)}
      data-volume={clip.volume.toFixed(2)}
      data-muted={clip.muted ? 'true' : 'false'}
      data-operation-status={operationState?.status ?? 'idle'}
      aria-disabled={isOperationRunning}
      onMouseDown={(e) => !isEditLocked && handleMouseDown(e, 'move')}
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Clip content */}
      <div
        className={`absolute inset-0 overflow-hidden px-1.5 py-1 pointer-events-none text-purple-300 ${
          clip.muted ? 'opacity-35' : 'opacity-80'
        }`}
        data-testid="timeline-audio-waveform-window"
        data-source-width={waveformSourceWidth.toFixed(3)}
        data-source-offset={waveformOffset.toFixed(3)}
      >
        <div
          className="h-full"
          style={{
            width: `${waveformSourceWidth}px`,
            transform: `translateX(-${waveformOffset}px)`,
          }}
          data-testid="timeline-audio-waveform-source"
        >
          <StaticWaveform
            sourcePath={clip.sourcePath}
            barCount={36}
            amplitude={clip.muted ? 0 : clip.volume}
            gain={clip.muted ? undefined : clip.volume}
          />
        </div>
      </div>

      <TimelineVolumeHandle
        clipId={clip.id}
        clipType="audio"
        volume={clip.volume}
        visible={(isSelected || isHovered) && !isEditLocked}
        disabled={isEditLocked}
        testId="timeline-audio-volume-handle"
        onPreview={onVolumePreview}
        onCommit={onVolumeCommit}
      />

      <div className="relative z-10 h-full flex items-center justify-between px-2 py-1">
        {/* Clip info */}
        <div className="flex-1 min-w-0">
          <div className="text-foreground text-xs font-medium truncate drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]">
            {displayLabel}
          </div>
          <div className="text-purple-200 text-xs font-medium drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]">
            {plannedWindowS !== null
              ? `${formatDuration(effectiveDuration)} / ${formatDuration(plannedWindowS)}`
              : formatDuration(effectiveDuration)}
          </div>
        </div>

        {/* Speed badge + muted indicator */}
        <div className="flex items-center gap-1 ml-1">
          {clip.muted && (
            <span
              className="inline-flex h-5 w-5 items-center justify-center rounded bg-surface-0/80 text-warning"
              data-testid="timeline-audio-clip-mute-badge"
              aria-label={t('timelineClip.audioMuted')}
            >
              <VolumeX className="h-3 w-3" aria-hidden="true" />
            </span>
          )}
          {clip.speed !== 1.0 && (
            <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-warning/80 text-white">
              {clip.speed}x
            </span>
          )}
          {operationState?.status === 'error' && (
            <span
              className="inline-flex h-5 w-5 items-center justify-center rounded bg-surface-0/80 text-destructive"
              data-testid="timeline-audio-clip-operation-error"
              aria-label="Audio action failed"
            >
              <AlertTriangle className="h-3 w-3" aria-hidden="true" />
            </span>
          )}
        </div>
      </div>

      {isOperationRunning && (
        <div
          className="pointer-events-none absolute inset-0 z-40 flex items-center justify-center bg-background/60"
          data-testid="timeline-audio-clip-operation-running"
          role="status"
          aria-live="polite"
          aria-label="Audio action running"
        >
          <div className="flex h-7 w-7 items-center justify-center rounded-full border border-primary/50 bg-surface-0/90 text-primary shadow-xl">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          </div>
        </div>
      )}

      {/* Left edge resize handle */}
      {!isEditLocked && (
        <div
          className="absolute left-0 top-0 z-20 h-full w-2 cursor-ew-resize hover:bg-purple-400/30"
          style={{ width: `${EDGE_RESIZE_WIDTH}px` }}
          data-testid="timeline-audio-trim-start"
          onMouseDown={(e) => handleEdgeMouseDown(e, 'start')}
        />
      )}

      {/* Right edge resize handle */}
      {!isEditLocked && (
        <div
          className="absolute right-0 top-0 z-20 h-full w-2 cursor-ew-resize hover:bg-purple-400/30"
          style={{ width: `${EDGE_RESIZE_WIDTH}px` }}
          data-testid="timeline-audio-trim-end"
          onMouseDown={(e) => handleEdgeMouseDown(e, 'end')}
        />
      )}
    </div>
  )
})

function formatDuration(seconds: number): string {
  if (seconds < 60) {
    return `${seconds.toFixed(1)}s`
  }
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}
